/**
 * 客戶模組 - 常量配置
 * @module customer/constants
 * @description 定義客戶管理相關的所有常量、配置與預設值
 */

import type { LevelConfig, StatusConfig, CustomerFormData, MemberLevel, CustomerStatus } from "./types";

/**
 * 會員等級配置
 * @description 定義五種會員等級的完整顯示配置
 */
export const levelConfig: Record<MemberLevel, LevelConfig> = {
  diamond: {
    label: "鑽石會員",
    icon: "💎",
    color: "text-cyan-600",
    bgColor: "bg-cyan-50",
    description: "製作公司、導演、製片",
  },
  gold: {
    label: "儲值會員",
    icon: "👑",
    color: "text-amber-600",
    bgColor: "bg-amber-50",
    description: "好客戶",
  },
  silver: {
    label: "同行會員",
    icon: "🤝",
    color: "text-slate-600",
    bgColor: "bg-slate-50",
    description: "同業調貨",
  },
  normal: {
    label: "一般會員",
    icon: "👤",
    color: "text-gray-600",
    bgColor: "bg-gray-50",
    description: "一般客戶",
  },
  problem: {
    label: "問題會員",
    icon: "⚠️",
    color: "text-red-600",
    bgColor: "bg-red-50",
    description: "需特別注意",
  },
};

/**
 * 客戶狀態配置
 * @description 定義三種客戶狀態的顯示配置
 */
export const statusConfig: Record<CustomerStatus, StatusConfig> = {
  active: {
    label: "活躍",
    color: "text-emerald-700",
    bgColor: "bg-emerald-100",
  },
  inactive: {
    label: "不活躍",
    color: "text-gray-700",
    bgColor: "bg-gray-100",
  },
  blocked: {
    label: "已封鎖",
    color: "text-red-700",
    bgColor: "bg-red-100",
  },
};

/**
 * 預設頭貼清單
 * @description 提供 6 組預設頭貼供使用者選擇
 */
export const defaultAvatars: readonly string[] = [
  "https://readdy.ai/api/search-image?query=professional%20business%20person%20silhouette%20avatar%20icon%20minimal%20style%20gray%20background&width=200&height=200&seq=avatar1&orientation=squarish",
  "https://readdy.ai/api/search-image?query=professional%20filmmaker%20director%20avatar%20icon%20minimal%20style%20gray%20background&width=200&height=200&seq=avatar2&orientation=squarish",
  "https://readdy.ai/api/search-image?query=professional%20photographer%20avatar%20icon%20minimal%20style%20gray%20background&width=200&height=200&seq=avatar3&orientation=squarish",
  "https://readdy.ai/api/search-image?query=professional%20video%20producer%20avatar%20icon%20minimal%20style%20gray%20background&width=200&height=200&seq=avatar4&orientation=squarish",
  "https://readdy.ai/api/search-image?query=professional%20creative%20director%20avatar%20icon%20minimal%20style%20gray%20background&width=200&height=200&seq=avatar5&orientation=squarish",
  "https://readdy.ai/api/search-image?query=professional%20studio%20manager%20avatar%20icon%20minimal%20style%20gray%20background&width=200&height=200&seq=avatar6&orientation=squarish",
];

/**
 * 新增客戶表單初始值
 * @description 定義新增客戶表單的預設值
 */
export const initialNewCustomerForm: CustomerFormData = {
  name: "",
  email: "",
  phone: "",
  company: "",
  tax_id: "",
  address: "",
  member_level: "normal",
  discount: 100,
  status: "active",
  tags: [],
  notes: "",
  createAuth: false,
  password: "",
};

/**
 * 折扣選項陣列
 * @description 提供可選的折扣百分比選項（40% - 100%）
 */
export const discountOptions: readonly number[] = [
  40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100,
] as const;

/**
 * 快速標籤建議清單
 * @description 提供常用的客戶標籤建議
 */
export const quickTagSuggestions: readonly string[] = [
  "VIP",
  "長期合作",
  "電影製作",
  "廣告拍攝",
  "MV製作",
  "紀錄片",
  "婚禮攝影",
  "商業攝影",
] as const;

/**
 * 密碼最小長度
 * @description 建立登入帳號時的密碼最小長度要求
 */
export const MIN_PASSWORD_LENGTH = 6;

/**
 * 頭貼壓縮尺寸
 * @description 上傳頭貼時的壓縮目標尺寸（正方形）
 */
export const AVATAR_SIZE = 200;

/**
 * 頭貼壓縮品質
 * @description WebP 格式的壓縮品質（0-1）
 */
export const AVATAR_QUALITY = 0.85;

/**
 * 標籤最大顯示數量
 * @description 列表中每個客戶最多顯示的標籤數量
 */
export const MAX_VISIBLE_TAGS = 3;

/**
 * 支援的頭貼圖片格式
 * @description 上傳頭貼時允許的 MIME 類型
 */
export const ALLOWED_AVATAR_TYPES: readonly string[] = [
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;

/**
 * Supabase Storage 頭貼路徑
 * @description 頭貼上傳至 Supabase Storage 的路徑前綴
 */
export const AVATAR_STORAGE_PATH = "avatars";

/**
 * Supabase Storage Bucket 名稱
 * @description 頭貼儲存的 Bucket 名稱
 */
export const AVATAR_STORAGE_BUCKET = "product-images";

/**
 * 折扣顏色對應表
 * @description 根據折扣百分比返回對應的 Tailwind 顏色類別
 */
export const discountColorMap = {
  high: { threshold: 70, class: "text-cyan-600 font-bold" },      // 折扣 ≤ 70%
  medium: { threshold: 80, class: "text-amber-600 font-semibold" }, // 折扣 ≤ 80%
  low: { threshold: 90, class: "text-slate-600 font-medium" },     // 折扣 ≤ 90%
  none: { class: "text-gray-500" },                                 // 折扣 > 90%
} as const;
